import * as React from "react";
import {
  Archive,
  ArchiveRestore,
  ArrowDown,
  ArrowUp,
  Blocks,
  ChevronRight,
  Plus,
  Trash2,
} from "lucide-react";
import type { ZodError } from "zod";
import {
  AREA_SCAN_DEFAULTS,
  HEIGHTS_DEFAULTS,
  MAX_CHECKLIST_ITEMS,
  MAX_MODEL_IMAGES,
  MODULE_CONFIG_SCHEMAS,
  checklistCode,
  isAssessmentModuleCode,
  type AdminModule,
  type ChecklistItem,
} from "@taskiq/shared";
import { Button } from "@/components/ui/Button";
import { Card, CardBody } from "@/components/ui/Card";
import { EmptyState, FormMessage } from "@/components/ui/Feedback";
import { Field, Form, FormSection, Input } from "@/components/ui/Field";
import { IconButton } from "@/components/ui/IconButton";
import { Switch } from "@/components/ui/Switch";
import { useToast } from "@/components/ui/toast-context";
import { api, apiErrorMessage, apiFieldErrors } from "@/lib/api";

/**
 * `assessment_modules.config`, as a typed form per module.
 *
 * ── The whole object goes back, and keys this form does not know survive ────
 *
 * `PATCH /modules/:code/config` takes the full config, not a diff. So every
 * form here spreads what is stored, then what it edited, then what the shared
 * schema parsed out of that - which adds the trimming and the defaults. A key a
 * later release adds, and this console has never heard of, is carried through a
 * save untouched rather than silently dropped by the first person to fix a typo.
 *
 * ── Validated here with the server's own schema ─────────────────────────────
 *
 * `MODULE_CONFIG_SCHEMAS` is what the API parses with, so a form that passes
 * here fails there only on the one rule the schema cannot see: a checklist code
 * that existed before and is missing after. The editor below makes that hard to
 * do - a saved item can be retired, never deleted - and the server's refusal
 * lands in the form message if it happens anyway.
 */

type Errors = Record<string, string>;

interface FormProps {
  module: AdminModule;
  onDirtyChange: (dirty: boolean) => void;
  onSaved: (module: AdminModule) => void;
}

export function ModuleConfigForm(props: FormProps) {
  switch (props.module.code) {
    case "area_scan":
      return <AreaScanForm {...props} />;
    case "heights":
      return <HeightsForm {...props} />;
    case "ppe":
      return <PpeForm {...props} />;
    case "utility":
      return <UtilityForm {...props} />;
    default:
      return (
        <Card>
          <EmptyState
            icon={<Blocks />}
            title="No editor for this module"
            description="Its configuration is not one this console knows how to edit. It stays in force as stored."
          />
        </Card>
      );
  }
}

/* ────────────────────────────── the plumbing ────────────────────────────── */

/**
 * Validate the whole config and send it. Errors come back keyed by the issue's
 * full path - `checks.2.label` - so a row can find its own.
 */
function useConfigSave(module: AdminModule, onSaved: (module: AdminModule) => void) {
  const toast = useToast();
  const [errors, setErrors] = React.useState<Errors>({});
  const [formError, setFormError] = React.useState<string | null>(null);
  const [saving, setSaving] = React.useState(false);

  async function save(edited: Record<string, unknown>, local: Errors = {}) {
    setFormError(null);
    if (!isAssessmentModuleCode(module.code)) return;

    const parsed = MODULE_CONFIG_SCHEMAS[module.code].safeParse({ ...module.config, ...edited });
    if (!parsed.success || Object.keys(local).length > 0) {
      const found = { ...(parsed.success ? {} : issuesByPath(parsed.error)), ...local };
      setErrors(found);
      setFormError(found._form ?? "Nothing was saved. Fix the fields marked below.");
      return;
    }
    setErrors({});

    setSaving(true);
    try {
      const result = await api<{ module: AdminModule }>(`/modules/${module.code}/config`, {
        method: "PATCH",
        body: { config: { ...module.config, ...(parsed.data as Record<string, unknown>) } },
      });
      toast.success(`${module.label} settings saved`, {
        description: "In force for every customer from the next check.",
      });
      onSaved(result.module);
    } catch (err) {
      setErrors(apiFieldErrors(err));
      setFormError(apiErrorMessage(err, "Could not save these settings"));
    } finally {
      setSaving(false);
    }
  }

  function reset() {
    setErrors({});
    setFormError(null);
  }

  return { errors, setErrors, formError, saving, save, reset };
}

function issuesByPath(error: ZodError): Errors {
  const out: Errors = {};
  for (const issue of error.issues) {
    const key = issue.path.join(".") || "_form";
    if (!(key in out)) out[key] = issue.message;
  }
  return out;
}

function useReportDirty(dirty: boolean, onDirtyChange: (dirty: boolean) => void) {
  React.useEffect(() => onDirtyChange(dirty), [dirty, onDirtyChange]);
}

function without(errors: Errors, ...keys: string[]): Errors {
  const next = { ...errors };
  for (const key of keys) delete next[key];
  return next;
}

/**
 * The card every module's form sits in, with the one pair of buttons.
 *
 * No header. The module's name is the panel's heading already, and a title and
 * a sentence over each form were the clutter: what a field is for sits behind
 * the mark beside its label instead.
 */
function ConfigCard({
  dirty,
  saving,
  formError,
  onSubmit,
  onDiscard,
  children,
}: {
  dirty: boolean;
  saving: boolean;
  formError: string | null;
  onSubmit: () => void;
  onDiscard: () => void;
  children: React.ReactNode;
}) {
  return (
    <Card>
      <CardBody>
        <Form
          onSubmit={(event) => {
            event.preventDefault();
            onSubmit();
          }}
        >
          {children}

          <FormMessage>{formError}</FormMessage>

          <div className="form-actions">
            <Button variant="secondary" size="sm" disabled={!dirty || saving} onClick={onDiscard}>
              Discard
            </Button>
            <Button type="submit" size="sm" loading={saving} disabled={!dirty}>
              Save changes
            </Button>
          </div>
        </Form>
      </CardBody>
    </Card>
  );
}

/* ─────────────────────────────── numbers ────────────────────────────────── */

interface NumberSpec<K extends string> {
  key: K;
  label: string;
  /** Behind the mark beside the label. What the value does, and its range where that is not obvious. */
  info: string;
}

/** A stored number as the text an input holds; anything else is blank. */
function numberText(value: unknown): string {
  return typeof value === "number" && Number.isFinite(value) ? String(value) : "";
}

function readNumbers<K extends string>(
  config: Record<string, unknown>,
  fields: readonly NumberSpec<K>[]
): Record<K, string> {
  return Object.fromEntries(
    fields.map((field) => [field.key, numberText(config[field.key])])
  ) as Record<K, string>;
}

/**
 * The typed text back to numbers. A blank field is its default, **written out**
 * rather than left absent: the spread under the edit is the stored config, and
 * an absent key would keep whatever older value is stored there.
 */
function toNumbers<K extends string>(
  values: Record<K, string>,
  fields: readonly NumberSpec<K>[],
  defaults: Record<K, number>
): { edited: Record<string, unknown>; local: Errors } {
  const edited: Record<string, unknown> = {};
  const local: Errors = {};
  for (const field of fields) {
    const raw = values[field.key].trim();
    if (raw === "") {
      edited[field.key] = defaults[field.key];
      continue;
    }
    const number = Number(raw);
    if (Number.isFinite(number)) edited[field.key] = number;
    else local[field.key] = "Enter a number";
  }
  return { edited, local };
}

/**
 * Text inputs with a decimal keypad, not `type="number"`: the native spinners
 * are the one part of a number input the theme cannot reach, and nothing else
 * in the console uses one - the code and phone fields are text with an
 * `inputMode` too. `toNumbers` does the parsing, and says so when it cannot.
 *
 * The default is the placeholder, so a blank field shows the value it will save.
 */
function NumberFields<K extends string>({
  idPrefix,
  fields,
  defaults,
  values,
  errors,
  onChange,
}: {
  idPrefix: string;
  fields: readonly NumberSpec<K>[];
  defaults: Record<K, number>;
  values: Record<K, string>;
  errors: Errors;
  onChange: (key: K, value: string) => void;
}) {
  return (
    <div className="form-grid">
      {fields.map((field) => {
        const id = `${idPrefix}-${field.key}`;
        return (
          <Field
            key={field.key}
            label={field.label}
            htmlFor={id}
            info={field.info}
            error={errors[field.key]}
          >
            <Input
              id={id}
              inputMode="decimal"
              autoComplete="off"
              placeholder={String(defaults[field.key])}
              invalid={Boolean(errors[field.key])}
              value={values[field.key]}
              onChange={(event) => onChange(field.key, event.target.value)}
            />
          </Field>
        );
      })}
    </div>
  );
}

/* ─────────────────────────────── area scan ──────────────────────────────── */

type AreaKey = keyof typeof AREA_SCAN_DEFAULTS;

/**
 * Every threshold the phone's frame selection reads. It runs on the handset,
 * because the gyroscope that says how the camera moved only exists there - so
 * tuning is a config edit, and only a change to the algorithm needs a release.
 */
const AREA_FIELDS: readonly NumberSpec<AreaKey>[] = [
  {
    key: "min_seconds",
    label: "Shortest sweep (seconds)",
    info: "The phone will not finish a sweep sooner than this.",
  },
  {
    key: "max_seconds",
    label: "Longest sweep (seconds)",
    info: "30 at most. Nothing past 30 seconds of a clip is read.",
  },
  {
    key: "max_frames",
    label: "Key frames, at most",
    info: `Frames sent to the model. 1 to ${MAX_MODEL_IMAGES}, the most images Azure takes in one request.`,
  },
  {
    key: "min_frames",
    label: "Key frames, at least",
    info: "With fewer usable frames than this, the phone asks for a better sweep.",
  },
  {
    key: "min_view_change_deg",
    label: "Minimum view change (degrees)",
    info: "Similar frames pointing closer together than this count as one view.",
  },
  {
    key: "max_turn_rate_dps",
    label: "Maximum turn rate (degrees per second)",
    info: "Frames taken while turning faster than this are treated as motion blur.",
  },
  {
    key: "min_sharpness",
    label: "Minimum sharpness",
    info: "Laplacian variance on a 320px grayscale frame; frames below it are too soft. Tune from field data.",
  },
  {
    key: "min_sweep_deg",
    label: "Minimum sweep (degrees)",
    info: "Less total camera travel than this is not a sweep.",
  },
];

function AreaScanForm({ module, onDirtyChange, onSaved }: FormProps) {
  // A key missing from the stored row stays blank, with the default as its
  // placeholder - rows seeded before these thresholds existed carry only the
  // two durations, and the phone already falls back to the defaults for them.
  const [initial] = React.useState(() => readNumbers(module.config, AREA_FIELDS));
  const [values, setValues] = React.useState(initial);
  const { errors, setErrors, formError, saving, save, reset } = useConfigSave(module, onSaved);

  const dirty = AREA_FIELDS.some((field) => values[field.key] !== initial[field.key]);
  useReportDirty(dirty, onDirtyChange);

  function submit() {
    const { edited, local } = toNumbers(values, AREA_FIELDS, AREA_SCAN_DEFAULTS);
    void save(edited, local);
  }

  return (
    <ConfigCard
      dirty={dirty}
      saving={saving}
      formError={formError}
      onSubmit={submit}
      onDiscard={() => {
        setValues(initial);
        reset();
      }}
    >
      <NumberFields
        idPrefix="area-scan"
        fields={AREA_FIELDS}
        defaults={AREA_SCAN_DEFAULTS}
        values={values}
        errors={errors}
        onChange={(key, value) => {
          setValues((prev) => ({ ...prev, [key]: value }));
          if (errors[key]) setErrors((prev) => without(prev, key));
        }}
      />
    </ConfigCard>
  );
}

/* ──────────────────────────────── heights ───────────────────────────────── */

type HeightsKey = keyof typeof HEIGHTS_DEFAULTS;

const HEIGHTS_FIELDS: readonly NumberSpec<HeightsKey>[] = [
  {
    key: "target_angle_deg",
    label: "Target angle (degrees)",
    info: "What each reading is compared with, 45 to 90. 75.5 is the 4-to-1 rule.",
  },
  {
    key: "tolerance_deg",
    label: "Tolerance (± degrees)",
    info: "How far from the target still passes, 0.5 to 10.",
  },
  {
    key: "auto_capture_band_deg",
    label: "Auto-capture band (± degrees)",
    info: "How far from the target the phone takes the reading on its own. At least the tolerance.",
  },
];

function HeightsForm({ module, onDirtyChange, onSaved }: FormProps) {
  const [initial] = React.useState(() => ({
    numbers: readNumbers(module.config, HEIGHTS_FIELDS),
    list: readChecklist(module.config, "checks"),
  }));
  const [numbers, setNumbers] = React.useState(initial.numbers);
  const [list, setList] = React.useState(initial.list);
  const { errors, setErrors, formError, saving, save, reset } = useConfigSave(module, onSaved);

  const dirty =
    HEIGHTS_FIELDS.some((field) => numbers[field.key] !== initial.numbers[field.key]) ||
    checklistChanged(list, initial.list, "checks");
  useReportDirty(dirty, onDirtyChange);

  function submit() {
    // The angles have defaults; the checklist has none and stays required.
    const { edited, local } = toNumbers(numbers, HEIGHTS_FIELDS, HEIGHTS_DEFAULTS);
    void save(
      { ...edited, ...checklistConfig(list, "checks") },
      { ...local, ...checklistErrors(list, "checks") }
    );
  }

  return (
    <ConfigCard
      dirty={dirty}
      saving={saving}
      formError={formError}
      onSubmit={submit}
      onDiscard={() => {
        setNumbers(initial.numbers);
        setList(initial.list);
        reset();
      }}
    >
      <NumberFields
        idPrefix="heights"
        fields={HEIGHTS_FIELDS}
        defaults={HEIGHTS_DEFAULTS}
        values={numbers}
        errors={errors}
        onChange={(key, value) => {
          setNumbers((prev) => ({ ...prev, [key]: value }));
          if (errors[key]) setErrors((prev) => without(prev, key));
        }}
      />

      <FormSection title="Set-up checks" info={CHECKLIST_INFO}>
        <ChecklistEditor
          listKey="checks"
          value={list}
          errors={errors}
          onChange={(next) => {
            setList(next);
            setErrors((prev) => withoutChecklist(prev, "checks"));
          }}
        />
      </FormSection>
    </ConfigCard>
  );
}

/* ────────────────────────────────── PPE ─────────────────────────────────── */

function PpeForm({ module, onDirtyChange, onSaved }: FormProps) {
  const [initial] = React.useState(() => readChecklist(module.config, "items"));
  const [list, setList] = React.useState(initial);
  const { errors, setErrors, formError, saving, save, reset } = useConfigSave(module, onSaved);

  const dirty = checklistChanged(list, initial, "items");
  useReportDirty(dirty, onDirtyChange);

  return (
    <ConfigCard
      dirty={dirty}
      saving={saving}
      formError={formError}
      onSubmit={() => void save(checklistConfig(list, "items"), checklistErrors(list, "items"))}
      onDiscard={() => {
        setList(initial);
        reset();
      }}
    >
      <FormSection title="Items" info={CHECKLIST_INFO}>
        <ChecklistEditor
          listKey="items"
          value={list}
          errors={errors}
          onChange={(next) => {
            setList(next);
            setErrors((prev) => withoutChecklist(prev, "items"));
          }}
        />
      </FormSection>
    </ConfigCard>
  );
}

/* ──────────────────────────────── utility ───────────────────────────────── */

const MAX_SUGGESTIONS = 20;

interface Suggestion {
  key: string;
  text: string;
}

function UtilityForm({ module, onDirtyChange, onSaved }: FormProps) {
  const [initial] = React.useState<Suggestion[]>(() =>
    (Array.isArray(module.config.suggestions) ? module.config.suggestions : [])
      .filter((entry): entry is string => typeof entry === "string")
      .map((text) => ({ key: nextKey(), text }))
  );
  const [rows, setRows] = React.useState(initial);
  const { errors, setErrors, formError, saving, save, reset } = useConfigSave(module, onSaved);

  const texts = (list: Suggestion[]) => JSON.stringify(list.map((row) => row.text));
  const dirty = texts(rows) !== texts(initial);
  useReportDirty(dirty, onDirtyChange);

  function change(next: Suggestion[]) {
    setRows(next);
    setErrors((prev) =>
      Object.fromEntries(Object.entries(prev).filter(([key]) => !key.startsWith("suggestions")))
    );
  }

  return (
    <ConfigCard
      dirty={dirty}
      saving={saving}
      formError={formError}
      onSubmit={() => void save({ suggestions: rows.map((row) => row.text) })}
      onDiscard={() => {
        setRows(initial);
        reset();
      }}
    >
      <FormSection title="Suggestions" info="One-tap chips on the phone's capture screen.">
        <div className="suggestion-list">
          <ol className="suggestion-rows">
            {rows.map((row, index) => {
              const error = errors[`suggestions.${index}`];
              return (
                <li key={row.key} className="suggestion-row">
                  <Input
                    inputSize="sm"
                    aria-label={`Suggestion ${index + 1}`}
                    placeholder="Electric meter"
                    maxLength={60}
                    autoFocus={row.text === "" && index === rows.length - 1}
                    invalid={Boolean(error)}
                    value={row.text}
                    onChange={(event) =>
                      change(
                        rows.map((r, i) => (i === index ? { ...r, text: event.target.value } : r))
                      )
                    }
                  />
                  <RowOrder
                    index={index}
                    count={rows.length}
                    name={row.text || `suggestion ${index + 1}`}
                    onMove={(by) => change(moved(rows, index, by))}
                  >
                    <IconButton
                      size="sm"
                      tone="danger"
                      label={`Remove ${row.text || `suggestion ${index + 1}`}`}
                      onClick={() => change(rows.filter((_, i) => i !== index))}
                    >
                      <Trash2 />
                    </IconButton>
                  </RowOrder>
                  {error && (
                    <p role="alert" className="row-error editor-row__error">
                      {error}
                    </p>
                  )}
                </li>
              );
            })}
          </ol>

          {errors.suggestions && (
            <p role="alert" className="row-error">
              {errors.suggestions}
            </p>
          )}

          <div className="editor-foot">
            <Button
              variant="secondary"
              size="sm"
              icon={<Plus />}
              disabled={rows.length >= MAX_SUGGESTIONS}
              onClick={() => change([...rows, { key: nextKey(), text: "" }])}
            >
              Add suggestion
            </Button>
            <span className="editor-foot__count tnum">
              {rows.length} of {MAX_SUGGESTIONS}
            </span>
          </div>
        </div>
      </FormSection>
    </ConfigCard>
  );
}

/* ─────────────────────────────── checklists ─────────────────────────────── */

const CHECKLIST_INFO =
  "New items reach the model on the next check, with no prompt change. " +
  "Technician confirms: answered by hand, never asked of the model. " +
  "Retired items keep their labels on old records.";

/**
 * One live row. `saved` is whether the code has ever been written: a saved code
 * is pointed at by detection rows forever, so it is read-only and the row can
 * only be retired, never removed. A new row's code follows its label until
 * somebody types in the code field.
 */
interface ChecklistRow {
  key: string;
  code: string;
  label: string;
  /** The stored label, for retiring a row whose edited label is not valid. */
  original: string;
  attested: boolean;
  saved: boolean;
  codeEdited: boolean;
}

interface Checklist {
  live: ChecklistRow[];
  retired: ChecklistItem[];
}

let keySeq = 0;
function nextKey(): string {
  keySeq += 1;
  return `row-${keySeq}`;
}

function readItems(raw: unknown): ChecklistItem[] {
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((entry) => {
    if (!entry || typeof entry !== "object") return [];
    const { code, label } = entry as { code?: unknown; label?: unknown };
    if (typeof code !== "string") return [];
    return [{ code, label: typeof label === "string" ? label : code }];
  });
}

function readChecklist(config: Record<string, unknown>, listKey: "items" | "checks"): Checklist {
  const attested = new Set(Array.isArray(config.attested) ? config.attested.map(String) : []);
  return {
    live: readItems(config[listKey]).map((item) => ({
      key: nextKey(),
      code: item.code,
      label: item.label,
      original: item.label,
      attested: attested.has(item.code),
      saved: true,
      codeEdited: false,
    })),
    retired: readItems(config.retired),
  };
}

/**
 * The three keys a checklist owns. `attested` is rebuilt from the rows, so a
 * stored code that is no longer live - which the schema would refuse - is
 * dropped on the way out rather than blocking every save.
 */
function checklistConfig(list: Checklist, listKey: "items" | "checks"): Record<string, unknown> {
  return {
    [listKey]: list.live.map((row) => ({ code: row.code.trim(), label: row.label })),
    attested: list.live.filter((row) => row.attested).map((row) => row.code.trim()),
    retired: list.retired,
  };
}

function checklistChanged(a: Checklist, b: Checklist, listKey: "items" | "checks"): boolean {
  return (
    JSON.stringify(checklistConfig(a, listKey)) !== JSON.stringify(checklistConfig(b, listKey))
  );
}

/**
 * What the schema reports only as "appears twice" on the whole list, pinned to
 * the new row that caused it - plus the empty list, which it words for zod.
 */
function checklistErrors(list: Checklist, listKey: "items" | "checks"): Errors {
  const out: Errors = {};
  if (list.live.length === 0) out[listKey] = "Keep at least one live item.";

  const retired = new Set(list.retired.map((item) => item.code));
  const counts = new Map<string, number>();
  for (const code of [...list.live.map((row) => row.code.trim()), ...retired]) {
    counts.set(code, (counts.get(code) ?? 0) + 1);
  }

  list.live.forEach((row, index) => {
    if (row.saved) return;
    const code = row.code.trim();
    const key = `${listKey}.${index}.code`;
    const parsed = checklistCode.safeParse(code);
    if (!code) out[key] = "Give it a code.";
    else if (!parsed.success) out[key] = parsed.error.issues[0]?.message ?? "Not a valid code.";
    else if (retired.has(code))
      out[key] = "A retired item already uses this code. Restore it instead.";
    else if ((counts.get(code) ?? 0) > 1) out[key] = "Another item already uses this code.";
  });
  return out;
}

function withoutChecklist(errors: Errors, listKey: string): Errors {
  return Object.fromEntries(
    Object.entries(errors).filter(
      ([key]) =>
        !key.startsWith(listKey) && !key.startsWith("attested") && !key.startsWith("retired")
    )
  );
}

/** A code from a label: the same shape `checklistCode` accepts, or empty. */
function toCode(label: string): string {
  return label
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^[^a-z]+/, "")
    .slice(0, 40)
    .replace(/_+$/, "");
}

function moved<T>(list: T[], index: number, by: -1 | 1): T[] {
  const target = index + by;
  if (target < 0 || target >= list.length) return list;
  const next = [...list];
  [next[index], next[target]] = [next[target]!, next[index]!];
  return next;
}

/** Up, down, and whatever the row's own last action is. */
function RowOrder({
  index,
  count,
  name,
  onMove,
  children,
}: {
  index: number;
  count: number;
  name: string;
  onMove: (by: -1 | 1) => void;
  children: React.ReactNode;
}) {
  return (
    <div className="row-actions">
      <IconButton
        size="sm"
        label={`Move ${name} up`}
        disabled={index === 0}
        onClick={() => onMove(-1)}
      >
        <ArrowUp />
      </IconButton>
      <IconButton
        size="sm"
        label={`Move ${name} down`}
        disabled={index === count - 1}
        onClick={() => onMove(1)}
      >
        <ArrowDown />
      </IconButton>
      {children}
    </div>
  );
}

/**
 * A module's checklist: the live items in the order the phone lists them, and
 * the retired ones folded away underneath.
 *
 * Retiring is not confirmed. It is undone by Restore, in the section below,
 * and a confirmation on a reversible step trains people to click through the
 * one that is not.
 */
function ChecklistEditor({
  listKey,
  value,
  errors,
  onChange,
}: {
  listKey: "items" | "checks";
  value: Checklist;
  errors: Errors;
  onChange: (next: Checklist) => void;
}) {
  const { live, retired } = value;
  const full = live.length >= MAX_CHECKLIST_ITEMS;

  function update(index: number, patch: Partial<ChecklistRow>) {
    onChange({
      ...value,
      live: live.map((row, i) => (i === index ? { ...row, ...patch } : row)),
    });
  }

  function retire(index: number) {
    const row = live[index]!;
    const label = row.label.trim();
    onChange({
      live: live.filter((_, i) => i !== index),
      retired: [...retired, { code: row.code, label: label.length >= 2 ? label : row.original }],
    });
  }

  function restore(index: number) {
    const item = retired[index]!;
    onChange({
      live: [
        ...live,
        {
          key: nextKey(),
          code: item.code,
          label: item.label,
          original: item.label,
          attested: false,
          saved: true,
          codeEdited: false,
        },
      ],
      retired: retired.filter((_, i) => i !== index),
    });
  }

  function add() {
    onChange({
      ...value,
      live: [
        ...live,
        {
          key: nextKey(),
          code: "",
          label: "",
          original: "",
          attested: false,
          saved: false,
          codeEdited: false,
        },
      ],
    });
  }

  // Everything the schema says about the list as a whole, rather than one row.
  const listErrors = Object.entries(errors).filter(
    ([key]) =>
      key === listKey ||
      key === "attested" ||
      key.startsWith("attested.") ||
      key.startsWith("retired")
  );

  return (
    <div className="checklist-editor">
      <div className="checklist-head" aria-hidden>
        <span>Code</span>
        <span>Label</span>
      </div>

      <ol className="checklist-rows">
        {live.map((row, index) => {
          const codeError = errors[`${listKey}.${index}.code`];
          const labelError = errors[`${listKey}.${index}.label`];
          const name = row.label.trim() || row.code || `item ${index + 1}`;
          return (
            <li key={row.key} className="checklist-row">
              {row.saved ? (
                <code className="checklist-row__code">{row.code}</code>
              ) : (
                <Input
                  inputSize="sm"
                  className="mono"
                  aria-label={`Code for ${name}`}
                  placeholder="from_the_label"
                  maxLength={40}
                  invalid={Boolean(codeError)}
                  value={row.code}
                  onChange={(event) =>
                    update(index, {
                      code: event.target.value,
                      // Emptied, it goes back to following the label.
                      codeEdited: event.target.value !== "",
                    })
                  }
                />
              )}

              <Input
                inputSize="sm"
                aria-label={row.saved ? `Label for ${row.code}` : `Label for new item ${index + 1}`}
                placeholder="What the technician reads"
                maxLength={60}
                autoFocus={!row.saved && row.label === "" && index === live.length - 1}
                invalid={Boolean(labelError)}
                value={row.label}
                onChange={(event) => {
                  const label = event.target.value;
                  update(
                    index,
                    !row.saved && !row.codeEdited ? { label, code: toCode(label) } : { label }
                  );
                }}
              />

              <Switch
                inline
                label="Technician confirms"
                checked={row.attested}
                onChange={(attested) => update(index, { attested })}
              />

              <RowOrder
                index={index}
                count={live.length}
                name={name}
                onMove={(by) => onChange({ ...value, live: moved(live, index, by) })}
              >
                {row.saved ? (
                  <IconButton size="sm" label={`Retire ${name}`} onClick={() => retire(index)}>
                    <Archive />
                  </IconButton>
                ) : (
                  // Never saved, so nothing points at it and it can simply go.
                  <IconButton
                    size="sm"
                    tone="danger"
                    label={`Remove ${name}`}
                    onClick={() => onChange({ ...value, live: live.filter((_, i) => i !== index) })}
                  >
                    <Trash2 />
                  </IconButton>
                )}
              </RowOrder>

              {(codeError || labelError) && (
                <p role="alert" className="row-error editor-row__error">
                  {codeError ?? labelError}
                </p>
              )}
            </li>
          );
        })}
      </ol>

      {listErrors.map(([key, message]) => (
        <p key={key} role="alert" className="row-error">
          {message}
        </p>
      ))}

      <div className="editor-foot">
        <Button variant="secondary" size="sm" icon={<Plus />} disabled={full} onClick={add}>
          Add item
        </Button>
        <span className="editor-foot__count tnum">
          {live.length} of {MAX_CHECKLIST_ITEMS}
        </span>
      </div>

      {retired.length > 0 && (
        <details className="retired">
          <summary className="retired__summary">
            <ChevronRight aria-hidden className="retired__chevron" />
            Retired <span className="retired__count tnum">{retired.length}</span>
          </summary>
          <ul className="retired__list">
            {retired.map((item, index) => (
              <li key={item.code} className="retired__row">
                <span className="retired__label">{item.label}</span>
                <code className="checklist-row__code">{item.code}</code>
                <Button
                  variant="ghost"
                  size="sm"
                  icon={<ArchiveRestore />}
                  disabled={full}
                  onClick={() => restore(index)}
                >
                  Restore
                </Button>
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}
